import { useMemo, useState } from 'react';
import { Link, router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input } from '../../../components/ui';
import { Section } from '../../../components/layout/AppShell';
import { Eye, Pencil, Trash2, Building2, Search, X } from 'lucide-react';
import Icon from '../../../components/ui/Icon';
import { cn } from '../../../lib/cn';

export default function UsersIndex({ users, stats, organizations = [], current_organization_id }) {
  const setOrgFilter = (value) => {
    router.get('/admin/users', value ? { organization_id: value } : {}, { preserveState: true, replace: true });
  };

  const activeOrgId = current_organization_id ? String(current_organization_id) : '';

  return (
    <TeacherShell scrollTitle="Users">
      <TeacherPageHeader
        title="Users"
        subtitle="Manage users and send invitations"
        actions={
          <Link href="/admin/users/new">
            <Button>Create User</Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        <StatCard label="Teachers" value={stats.teachers} />
        <StatCard label="Students" value={stats.students} />
        <StatCard label="Admins" value={stats.admins} />
        <StatCard label="Total" value={stats.total} />
      </div>

      <Section title="Organizations">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <OrgCard
            name="All organizations"
            count={stats.total}
            active={!activeOrgId}
            onClick={() => setOrgFilter('')}
          />
          {organizations.map((org) => (
            <OrgCard
              key={org.id}
              name={org.name}
              count={org.user_count}
              logoUrl={org.logo_url}
              active={activeOrgId === String(org.id)}
              onClick={() => setOrgFilter(org.id)}
            />
          ))}
        </div>
      </Section>

      <Section title="Invite a Teacher">
        <InviteForm organizations={organizations} />
      </Section>

      <Section title="All Users">
        <UserSearchAndTable users={users} />
      </Section>

      <Section title="Manage Organizations">
        <ManageOrganizations organizations={organizations} />
      </Section>
    </TeacherShell>
  );
}

function OrgCard({ name, count, logoUrl, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left p-4 rounded-lg border transition-colors flex items-center gap-3',
        active
          ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700'
          : 'bg-white border-gray-200 hover:border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-600'
      )}
    >
      <OrgLogo logoUrl={logoUrl} name={name} size={40} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {count} {count === 1 ? 'member' : 'members'}
        </p>
      </div>
    </button>
  );
}

function OrgLogo({ logoUrl, name, size = 32 }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className="rounded object-cover bg-gray-100 dark:bg-gray-700 shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500 shrink-0"
      style={{ width: size, height: size }}
    >
      <Icon icon={Building2} size="sm" />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <Card className="p-4 text-center">
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-1">{label}</p>
    </Card>
  );
}

function InviteForm({ organizations = [] }) {
  const [email, setEmail] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    router.post('/admin/users/invite', { email, organization_id: organizationId }, {
      onFinish: () => {
        setIsSubmitting(false);
        setEmail('');
        setOrganizationId('');
      },
    });
  };

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email address
          </label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teacher@school.edu"
            required
          />
        </div>
        {organizations.length > 0 && (
          <div className="flex-1">
            <label htmlFor="invite-organization" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Organization
            </label>
            <select
              id="invite-organization"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-600 shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] text-gray-900 dark:text-white transition-all duration-200 focus:outline-none focus:border-gray-400 focus:shadow-[inset_0_1px_3px_rgba(0,0,0,0.06),0_0_0_3px_rgba(156,163,175,0.2)] dark:focus:border-gray-500 dark:focus:shadow-[inset_0_1px_3px_rgba(0,0,0,0.06),0_0_0_3px_rgba(107,114,128,0.3)]"
            >
              <option value="">No organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Inviting...' : 'Invite Teacher'}
        </Button>
      </form>
    </Card>
  );
}

function ManageOrganizations({ organizations }) {
  const [editingId, setEditingId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Card padding="none">
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {organizations.map((org) => (
          <li key={org.id} className="px-6 py-4">
            {editingId === org.id ? (
              <OrgForm
                org={org}
                onCancel={() => setEditingId(null)}
                onSubmit={(formData) => {
                  formData.append('_method', 'patch');
                  router.post(`/admin/organizations/${org.id}`, formData, {
                    forceFormData: true,
                    onSuccess: () => setEditingId(null),
                  });
                }}
              />
            ) : (
              <div className="flex items-center gap-4">
                <OrgLogo logoUrl={org.logo_url} name={org.name} size={40} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{org.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {org.user_count} {org.user_count === 1 ? 'member' : 'members'}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditingId(org.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title={`Edit ${org.name}`}
                  >
                    <Icon icon={Pencil} size="sm" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (org.user_count > 0) {
                        alert(`Cannot delete ${org.name}: it still has ${org.user_count} member${org.user_count === 1 ? '' : 's'}. Reassign them first.`);
                        return;
                      }
                      if (!confirm(`Delete ${org.name}?`)) return;
                      router.delete(`/admin/organizations/${org.id}`);
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title={`Delete ${org.name}`}
                  >
                    <Icon icon={Trash2} size="sm" />
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
        <li className="px-6 py-4">
          {createOpen ? (
            <OrgForm
              onCancel={() => setCreateOpen(false)}
              onSubmit={(formData) => {
                router.post('/admin/organizations', formData, {
                  forceFormData: true,
                  onSuccess: () => setCreateOpen(false),
                });
              }}
            />
          ) : (
            <Button type="button" variant="ghost" onClick={() => setCreateOpen(true)}>
              + New organization
            </Button>
          )}
        </li>
      </ul>
    </Card>
  );
}

function OrgForm({ org, onSubmit, onCancel }) {
  const [name, setName] = useState(org?.name || '');
  const [logo, setLogo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    const fd = new FormData();
    fd.append('organization[name]', name.trim());
    if (logo) fd.append('organization[logo]', logo);
    onSubmit(fd);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row sm:items-end gap-3">
      <div className="flex-1">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Organization name"
          required
        />
      </div>
      <div className="flex-1">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Logo {org?.logo_url && '(replace)'}
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setLogo(e.target.files[0] || null)}
          className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-200 file:text-xs file:font-medium hover:file:bg-gray-200 dark:hover:file:bg-gray-600"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting || !name.trim()}>
          {submitting ? 'Saving...' : org ? 'Save' : 'Create'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

const ROLE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'student', label: 'Students' },
  { value: 'teacher', label: 'Teachers' },
  { value: 'admin', label: 'Admins' },
];

function normalize(value) {
  return (value || '').toString().toLowerCase().trim();
}

function userMatchesQuery(user, tokens) {
  if (tokens.length === 0) return true;
  const haystack = [
    user.full_name,
    user.first_name,
    user.last_name,
    user.email,
    user.organization_name,
    user.grade_level != null ? `grade ${user.grade_level}` : '',
  ]
    .map(normalize)
    .join(' ');
  return tokens.every((token) => haystack.includes(token));
}

function UserSearchAndTable({ users }) {
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');

  const studentGrades = useMemo(() => {
    const grades = new Set();
    users.forEach((u) => {
      if (u.role === 'student' && u.grade_level != null) {
        grades.add(u.grade_level);
      }
    });
    return Array.from(grades).sort((a, b) => a - b);
  }, [users]);

  const filteredUsers = useMemo(() => {
    const tokens = normalize(query).split(/\s+/).filter(Boolean);
    return users.filter((user) => {
      if (roleFilter !== 'all' && user.role !== roleFilter) return false;
      if (
        roleFilter === 'student' &&
        gradeFilter !== 'all' &&
        String(user.grade_level) !== String(gradeFilter)
      ) {
        return false;
      }
      return userMatchesQuery(user, tokens);
    });
  }, [users, query, roleFilter, gradeFilter]);

  const showGradeFilter = roleFilter === 'student' && studentGrades.length > 0;
  const hasActiveFilters = query !== '' || roleFilter !== 'all' || gradeFilter !== 'all';

  const clearAll = () => {
    setQuery('');
    setRoleFilter('all');
    setGradeFilter('all');
  };

  return (
    <Card padding="none">
      <div className="p-4 border-b border-gray-100 dark:border-gray-700 space-y-3">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500">
            <Icon icon={Search} size="sm" />
          </div>
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, organization, or grade…"
            className="pl-10 pr-10"
            aria-label="Search users"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <Icon icon={X} size="sm" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Role:</span>
          {ROLE_FILTERS.map((option) => (
            <FilterChip
              key={option.value}
              label={option.label}
              active={roleFilter === option.value}
              onClick={() => {
                setRoleFilter(option.value);
                if (option.value !== 'student') setGradeFilter('all');
              }}
            />
          ))}
        </div>

        {showGradeFilter && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Grade:</span>
            <FilterChip
              label="All"
              active={gradeFilter === 'all'}
              onClick={() => setGradeFilter('all')}
            />
            {studentGrades.map((grade) => (
              <FilterChip
                key={grade}
                label={`Grade ${grade}`}
                active={String(gradeFilter) === String(grade)}
                onClick={() => setGradeFilter(grade)}
              />
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            Showing {filteredUsers.length} of {users.length}
            {users.length === 1 ? ' user' : ' users'}
          </span>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <UserTable users={filteredUsers} />
    </Card>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
        active
          ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
      )}
    >
      {label}
    </button>
  );
}

function UserTable({ users }) {
  const handleImpersonate = (userId) => {
    router.post(`/admin/users/${userId}/impersonate`);
  };

  const roleBadgeColors = {
    admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    teacher: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    student: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  };

  if (users.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        No users match the current filters.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
            <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
            <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organization</th>
            <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
            <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {users.map((user) => (
              <tr
                key={user.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => router.visit(`/admin/users/${user.id}`)}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium',
                        'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      )}
                    >
                      {user.initials}
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.full_name}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {user.email}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {user.organization_name || <span className="text-gray-300 dark:text-gray-600">--</span>}
                </td>
                <td className="px-6 py-4">
                  <span className={cn('text-xs font-medium px-2 py-0.5 rounded', roleBadgeColors[user.role] || 'bg-gray-100 text-gray-600')}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleImpersonate(user.id); }}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title={`Impersonate ${user.full_name}`}
                  >
                    <Icon icon={Eye} size="sm" />
                  </button>
                </td>
              </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
